import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Container, Reveal } from "@/components/Reveal";
import { GLOSSARY } from "@/data/education";

export default function Glossary() {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return GLOSSARY;
    return GLOSSARY.filter(
      (g) =>
        g.term.toLowerCase().includes(query) ||
        g.short.toLowerCase().includes(query) ||
        g.definition.toLowerCase().includes(query)
    );
  }, [q]);

  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((g) => {
      (map[g.group] = map[g.group] || []).push(g);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <>
      <SEO
        title="Commercial Energy Glossary"
        description="A searchable glossary of commercial energy terms — capacity, demand, therms, fixed vs. indexed pricing, deregulation, and more — each explained in plain language."
        path="/education/glossary"
      />
      <PageHero
        overline="Education Center"
        titleLines={["Commercial Energy", "Glossary"]}
        subtitle="Plain-language definitions of the terms that show up in commercial energy contracts, bills, and markets — each with why it matters and where to learn more."
        crumbs={[
          { label: "Education Center", path: "/education" },
          { label: "Glossary", path: "/education/glossary" },
        ]}
      />

      <section className="bg-white py-16 md:py-20">
        <Container>
          <div className="relative mx-auto max-w-xl">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slatey" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search glossary terms…"
              data-testid="glossary-search"
              className="w-full border border-line bg-white py-4 pl-14 pr-5 text-navy outline-none transition-colors placeholder:text-slatey/60 focus:border-electric"
            />
          </div>

          <p className="mt-6 text-center text-sm text-slatey" data-testid="glossary-count">
            {filtered.length} term{filtered.length !== 1 ? "s" : ""}
          </p>

          <div className="mt-12 space-y-14">
            {groups.map(([group, terms]) => (
              <div key={group}>
                <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-electric">
                  {group}
                </h2>
                <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {terms.map((g, i) => (
                    <Reveal key={g.slug} delay={(i % 3) * 0.04}>
                      <Link
                        to={`/education/glossary/${g.slug}`}
                        data-testid={`glossary-term-${g.slug}`}
                        className="group flex h-full flex-col border border-line p-6 transition-colors hover:border-electric"
                      >
                        <div className="flex items-start justify-between">
                          <h3 className="font-heading text-lg font-medium text-navy transition-colors group-hover:text-electric">
                            {g.term}
                          </h3>
                          <ArrowUpRight className="h-4 w-4 shrink-0 text-slatey transition-colors group-hover:text-electric" />
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-slatey">{g.short}</p>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-center text-slatey">No terms match “{q}”.</p>
            )}
          </div>
        </Container>
      </section>

      <CTABand />
    </>
  );
}
